import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';

import { archivedSearchService } from '@/services/search-service';
import { localDataService } from '@/providers/rxdb/local-data-service';
import { appReadyAtom } from '@/state';

// Shared by useArchivedSearch and useArchivedBrowseSearch: lazily starts the archived
// worker + ingestion on first use (ensureArchivedSearchIndex -> ensureStarted, complement
// hooks, reconcile) and tracks the archived instance's OWN IndexStatus. Callers stay
// "not ready" until the first reconcile completes, regardless of the deferred archived
// replication (no sync-completion signal exists, by design: the reconcile + live hooks
// absorb late-arriving docs).
export const useArchivedSearchIndex = (enabled: boolean): boolean => {
  const [indexReady, setIndexReady] = useState(false);
  const appReady = useAtomValue(appReadyAtom);

  useEffect(() => {
    // Gated on appReady (ensureArchivedSearchIndex needs initialized rxCollections, and an
    // effect that ran too early would otherwise never retry for this mount) and on enabled
    // (mounting disabled must not start the pipeline — mounting ≠ opening the archive).
    if (!appReady || !enabled) {
      return;
    }
    localDataService.ensureArchivedSearchIndex().catch((error) => {
      console.error('[ARCHIVED_SEARCH] Failed to start archived search index:', error);
    });
  }, [appReady, enabled]);

  useEffect(() => {
    const subscription = archivedSearchService.indexStatus.subscribe((status) => {
      setIndexReady(status === 'ready');
    });
    return () => subscription.unsubscribe();
  }, []);

  return indexReady;
};
